import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  fetchMediaPosts,
  fetchMediaTracks,
  type MediaPost,
  type MediaTrack,
} from '@/lib/media';
import UploadModal from '@/pages/media/components/UploadModal';

type ContentKind = 'video' | 'audio';

function DashboardMediaPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<MediaPost[]>([]);
  const [tracks, setTracks] = useState<MediaTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState<ContentKind>('video');

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [p, t] = await Promise.all([fetchMediaPosts(), fetchMediaTracks()]);
      setPosts(p.filter((x) => x.owner_id === user.id));
      setTracks(t.filter((x) => x.owner_id === user.id));
    } catch (err) {
      setError('Impossible de charger votre contenu. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeletePost = async (id: string) => {
    if (!confirm('Voulez-vous vraiment supprimer cette vidéo ?')) return;
    setError('');
    const { error: err } = await supabase.from('media_posts').delete().eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  const handleDeleteTrack = async (id: string) => {
    if (!confirm('Voulez-vous vraiment supprimer ce morceau ?')) return;
    setError('');
    const { error: err } = await supabase.from('media_tracks').delete().eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setTracks((prev) => prev.filter((t) => t.id !== id));
  };

  const allItems = kindFilter === 'video'
    ? posts.map((p) => ({ kind: 'video' as const, data: p }))
    : tracks.map((t) => ({ kind: 'audio' as const, data: t }));

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Mon contenu média</h1>
        <p className="text-sm text-foreground-400 mt-1">
          Gérez vos vidéos, acapellas, publicités et morceaux publiés sur la plateforme.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex gap-1 p-1 bg-background-100 rounded-lg">
          <button
            onClick={() => setKindFilter('video')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              kindFilter === 'video'
                ? 'bg-primary-500 text-foreground-950'
                : 'text-foreground-400 hover:text-foreground-50'
            }`}
          >
            <i className="ri-video-line mr-1.5" />
            Vidéos ({posts.length})
          </button>
          <button
            onClick={() => setKindFilter('audio')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              kindFilter === 'audio'
                ? 'bg-primary-500 text-foreground-950'
                : 'text-foreground-400 hover:text-foreground-50'
            }`}
          >
            <i className="ri-music-2-line mr-1.5" />
            Audio ({tracks.length})
          </button>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          className="px-5 py-2.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line" />
          Publier du contenu
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      ) : allItems.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-background-300/60 rounded-xl">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-gallery-line text-2xl text-foreground-400" />
          </div>
          <p className="text-foreground-500 font-medium mb-1">
            Aucun {kindFilter === 'video' ? 'contenu vidéo' : 'contenu audio'} pour le moment
          </p>
          <p className="text-sm text-foreground-500 mb-4">
            Publiez votre première création pour toucher la communauté.
          </p>
          <Link
            to="/media"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            <i className="ri-compass-3-line" />
            Explorer Média
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {allItems.map((item) => {
            if (item.kind === 'video') {
              const v = item.data as MediaPost;
              return (
                <div
                  key={v.id}
                  className="group relative rounded-lg overflow-hidden bg-background-100 border border-background-200/70 flex flex-col"
                >
                  <div className="relative aspect-video w-full overflow-hidden bg-background-200/60">
                    {v.thumbnail_url ? (
                      <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-video-line text-4xl text-foreground-400" />
                      </div>
                    )}
                    <span className="absolute top-2 left-2 px-2 py-1 rounded-md bg-foreground-950/70 text-foreground-50 text-xs font-medium">
                      {v.content_type === 'ad' ? 'Publicité' : v.category}
                    </span>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-medium text-sm text-foreground-50 mb-1 truncate">{v.title}</h3>
                    <p className="text-xs text-foreground-400 mb-2">{v.artist_name || '—'} · {v.location || '—'}</p>
                    <div className="mt-auto flex items-center justify-between text-xs text-foreground-400">
                      <span>{v.views} vues · {v.likes} j&apos;aime</span>
                      <span className={v.status === 'active' ? 'text-accent-400' : 'text-secondary-400'}>
                        {v.status === 'active' ? 'En ligne' : 'En attente'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeletePost(v.id)}
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-foreground-950/70 text-foreground-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Supprimer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              );
            }
            const t = item.data as MediaTrack;
            return (
              <div
                key={t.id}
                className="group relative rounded-lg overflow-hidden bg-background-100 border border-background-200/70 flex flex-col"
              >
                <div className="relative aspect-square w-full overflow-hidden bg-background-200/60">
                  {t.cover_url ? (
                    <img src={t.cover_url} alt={t.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-music-2-line text-4xl text-foreground-400" />
                    </div>
                  )}
                  <span className="absolute top-2 left-2 px-2 py-1 rounded-md bg-foreground-950/70 text-foreground-50 text-xs font-medium">
                    {t.content_type === 'acapella' ? 'Acapella' : t.category}
                  </span>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-medium text-sm text-foreground-50 mb-1 truncate">{t.title}</h3>
                  <p className="text-xs text-foreground-400 mb-2">{t.artist_name || '—'} · {t.location || '—'}</p>
                  <div className="mt-auto flex items-center justify-between text-xs text-foreground-400">
                    <span>{t.plays} écoutes · {t.likes} j&apos;aime</span>
                    <span className={t.status === 'active' ? 'text-accent-400' : 'text-secondary-400'}>
                      {t.status === 'active' ? 'En ligne' : 'En attente'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteTrack(t.id)}
                  className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-foreground-950/70 text-foreground-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Supprimer"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onPublished={loadData}
      />
    </DashboardLayout>
  );
}

export default DashboardMediaPage;